/**
 * Ops route — System metrics and status.
 */

import { useQuery } from "@tanstack/react-query";
import { Activity, HardDrive, Cpu, Server, Zap, Terminal as TerminalIcon, Wifi, WifiOff } from "lucide-react";
import { useState, useEffect } from "react";
import { useStatsStream, useEventStreamStatus } from "../hooks/useEventStream";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { StatusDot } from "../components/ui/StatusDot";

interface MemoryInfo {
  total: number;
  used: number;
  free: number;
  buffers: number;
  cached: number;
}

interface DiskInfo {
  mount: string;
  total: number;
  used: number;
  available: number;
  percent: number;
}

interface CpuInfo {
  count: number;
  model: string;
}

interface ServiceInfo {
  name: string;
  state: "running" | "stopped" | "failed" | "unknown";
  uptime: string | null;
}

interface ProcessInfo {
  pid: number;
  cpu: number;
  mem: number;
  command: string;
}

interface OpsStatus {
  uptime: number;
  loadAvg: [number, number, number];
  memory: MemoryInfo;
  disk: DiskInfo[];
  cpu: CpuInfo;
  services: ServiceInfo[];
  processes: ProcessInfo[];
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.join(" ") || "< 1m";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function ProgressBar({
  percent,
  variant = "default",
}: {
  percent: number;
  variant?: "default" | "warn" | "danger";
}) {
  const barColor =
    variant === "danger" || percent > 90
      ? "var(--bw-danger)"
      : variant === "warn" || percent > 75
        ? "var(--bw-warn)"
        : "var(--bw-accent)";

  return (
    <div className="w-full h-2 bg-[var(--bw-surface-2)] rounded-sm overflow-hidden">
      <div
        className="h-full transition-all duration-300"
        style={{
          width: `${Math.min(100, percent)}%`,
          backgroundColor: barColor,
        }}
      />
    </div>
  );
}

function getServiceStatus(state: ServiceInfo["state"]): "online" | "offline" | "error" | "warning" {
  switch (state) {
    case "running":
      return "online";
    case "stopped":
      return "offline";
    case "failed":
      return "error";
    default:
      return "warning";
  }
}

export function OpsRoute() {
  // Initial load via HTTP
  const { data: initialData, isLoading, error } = useQuery({
    queryKey: ["ops-status"],
    queryFn: async () => {
      const res = await fetch("/api/ops/status");
      return res.json() as Promise<OpsStatus>;
    },
    // No refetch - we'll use WebSocket for updates
    refetchInterval: false,
    staleTime: Infinity,
  });

  // Live stats via WebSocket
  const { stats: liveStats, isConnected: wsConnected } = useStatsStream();
  const { state: wsState } = useEventStreamStatus();

  // Merge initial data with live stats
  const [stats, setStats] = useState<{
    cpu: number;
    mem: number;
    disk: number;
    uptime: number;
    loadAvg: [number, number, number];
  } | null>(null);

  useEffect(() => {
    if (liveStats) {
      setStats({
        cpu: liveStats.cpu,
        mem: liveStats.mem,
        disk: liveStats.disk,
        uptime: liveStats.uptime,
        loadAvg: liveStats.loadAvg,
      });
    }
  }, [liveStats]);

  // Full data from initial query (for services, processes, etc.)
  const data = initialData;

  // Current stats (prefer live, fallback to initial)
  const currentUptime = stats?.uptime ?? data?.uptime ?? 0;
  const currentLoadAvg = stats?.loadAvg ?? data?.loadAvg ?? [0, 0, 0];
  const currentMem = stats?.mem ?? (data?.memory ? (data.memory.used / data.memory.total) * 100 : 0);

  if (isLoading) {
    return (
      <div className="p-4 md:p-6">
        <CRTHeader text="Operaciones" level={2} />
        <div className="mt-6 text-[var(--bw-muted)] animate-pulse">
          ESTABLECIENDO CONEXIÓN...
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <CRTHeader text="Operaciones" level={2} />
        <CorpoCard className="border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)]">
            ⚠ ERROR DE CONEXIÓN: {error instanceof Error ? error.message : "Sin datos"}
          </p>
        </CorpoCard>
      </div>
    );
  }

  const memoryPercent = currentMem;

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <CRTHeader text="Operaciones" level={2} />
        <div className="flex items-center gap-2">
          {wsConnected ? (
            <>
              <Wifi size={14} className="text-[var(--bw-success)]" />
              <span className="text-xs text-[var(--bw-success)] uppercase tracking-wider">LIVE</span>
            </>
          ) : (
            <>
              <WifiOff size={14} className="text-[var(--bw-muted)]" />
              <span className="text-xs text-[var(--bw-muted)] uppercase tracking-wider">
                {wsState === "connecting" || wsState === "reconnecting" ? "CONNECTING..." : "OFFLINE"}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Terminal section — first, most-used tool */}
      <TerminalSection />

      {/* Top stats grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Uptime */}
        <CorpoCard>
          <div className="flex items-center gap-3">
            <Activity size={20} className="text-[var(--bw-accent)]" />
            <div>
              <p className="text-xs text-[var(--bw-muted)] uppercase">Uptime</p>
              <p className="text-lg font-bold text-[var(--bw-fg)]">
                {formatUptime(currentUptime)}
              </p>
            </div>
          </div>
        </CorpoCard>

        {/* Load Average */}
        <CorpoCard>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-[var(--bw-accent)]" />
              <p className="text-xs text-[var(--bw-muted)] uppercase">Load AVG</p>
            </div>
            <div className="flex items-center gap-3">
              {currentLoadAvg.map((load, idx) => {
                const isHigh = load > data.cpu.count;
                return (
                  <div key={idx} className="flex items-center gap-1">
                    <StatusDot
                      status={isHigh ? "warning" : "online"}
                      size="sm"
                      pulse={isHigh}
                    />
                    <span className="text-sm font-mono text-[var(--bw-fg)]">
                      {load.toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </CorpoCard>

        {/* CPU */}
        <CorpoCard>
          <div className="flex items-center gap-3">
            <Cpu size={20} className="text-[var(--bw-accent)]" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-[var(--bw-muted)] uppercase">CPU</p>
              <p className="text-sm font-bold text-[var(--bw-fg)]">
                {data.cpu.count} cores
              </p>
              <p className="text-xs text-[var(--bw-muted)] truncate">
                {data.cpu.model}
              </p>
            </div>
          </div>
        </CorpoCard>

        {/* Memory */}
        <CorpoCard>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[var(--bw-muted)] uppercase">Memoria</p>
              <p className="text-xs text-[var(--bw-accent)]">
                {memoryPercent.toFixed(1)}%
              </p>
            </div>
            <ProgressBar percent={memoryPercent} />
            <p className="text-xs text-[var(--bw-muted)]">
              {formatBytes(data.memory.used)} / {formatBytes(data.memory.total)}
            </p>
          </div>
        </CorpoCard>
      </div>

      {/* Disk usage */}
      <CorpoCard title="ALMACENAMIENTO">
        {data.disk.length === 0 ? (
          <p className="text-[var(--bw-muted)]">Sin información de disco</p>
        ) : (
          <div className="space-y-4">
            {data.disk.map((disk) => (
              <div key={disk.mount} className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <HardDrive size={14} className="text-[var(--bw-muted)]" />
                    <span className="text-sm font-mono text-[var(--bw-fg)]">
                      {disk.mount}
                    </span>
                  </div>
                  <span className="text-xs text-[var(--bw-accent)]">
                    {disk.percent}%
                  </span>
                </div>
                <ProgressBar percent={disk.percent} />
                <p className="text-xs text-[var(--bw-muted)]">
                  {formatBytes(disk.used)} usado / {formatBytes(disk.total)} total
                </p>
              </div>
            ))}
          </div>
        )}
      </CorpoCard>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Services */}
        <CorpoCard title="SERVICIOS">
          {data.services.length === 0 ? (
            <p className="text-[var(--bw-muted)]">Sin servicios</p>
          ) : (
            <div className="space-y-2">
              {data.services.map((svc) => (
                <div
                  key={svc.name}
                  className="flex items-center justify-between py-1 border-b border-[var(--bw-border)] last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <Server size={14} className="text-[var(--bw-muted)]" />
                    <span className="text-sm font-mono text-[var(--bw-fg)]">
                      {svc.name}
                    </span>
                  </div>
                  <StatusDot
                    status={getServiceStatus(svc.state)}
                    size="sm"
                    label={svc.state.toUpperCase()}
                  />
                </div>
              ))}
            </div>
          )}
        </CorpoCard>

        {/* Top Processes */}
        <CorpoCard title="TOP PROCESOS">
          {data.processes.length === 0 ? (
            <p className="text-[var(--bw-muted)]">Sin procesos</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-[var(--bw-muted)] uppercase border-b border-[var(--bw-border)]">
                    <th className="py-2 pr-2">PID</th>
                    <th className="py-2 pr-2">CPU%</th>
                    <th className="py-2 pr-2">MEM%</th>
                    <th className="py-2">Command</th>
                  </tr>
                </thead>
                <tbody>
                  {data.processes.map((proc) => (
                    <tr
                      key={proc.pid}
                      className="border-b border-[var(--bw-border)] last:border-b-0"
                    >
                      <td className="py-1.5 pr-2 font-mono text-[var(--bw-accent)]">
                        {proc.pid}
                      </td>
                      <td className="py-1.5 pr-2 font-mono text-[var(--bw-fg)]">
                        {proc.cpu.toFixed(1)}
                      </td>
                      <td className="py-1.5 pr-2 font-mono text-[var(--bw-fg)]">
                        {proc.mem.toFixed(1)}
                      </td>
                      <td className="py-1.5 text-[var(--bw-muted)] truncate max-w-[200px]">
                        {proc.command}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CorpoCard>
      </div>

    </div>
  );
}

/**
 * Terminal section — links to the full-viewport Terminal Surface.
 *
 * The shell now lives on its own chrome-free route (/terminal), durable across
 * reconnects and usable from the installed mobile PWA — so this is a launcher,
 * not an embedded, size-capped, desktop-only widget.
 */
function TerminalSection() {
  return (
    <CorpoCard title="TERMINAL">
      <div className="flex items-center gap-3 py-2">
        <TerminalIcon size={20} className="text-[var(--bw-accent)]" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[var(--bw-fg)]">
            Shell interactivo a pantalla completa
          </p>
          <p className="text-xs text-[var(--bw-muted)] mt-1">
            Sesión durable: sobrevive reconexiones y funciona instalado en móvil.
          </p>
        </div>
        <a
          href="/terminal"
          className="shrink-0 px-3 py-1.5 rounded border border-[var(--bw-accent)] text-[var(--bw-accent)] text-xs uppercase tracking-wider hover:bg-[var(--bw-accent)] hover:text-[var(--bw-bg)] transition-colors"
        >
          Abrir
        </a>
      </div>
    </CorpoCard>
  );
}
